import { useState, useEffect, useMemo } from "react";
import MatchComponent from "../../components/match";
import TournamentEventComponent from "../../components/tournament-event";
import MatchUpdateDialog from "../../components/match-update-dialog";
import TournamentEventUpdateDialog from "../../components/tournament-event-update-dialog";
import { useAppSelector } from "../../hooks";
import { LocationType, Match, STATUS, TournamentEvent } from "../../types/types";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants";

interface Props {
  location?: LocationType;
  selectedLocation?: LocationType;
  isMatchesView: boolean;
}

function MatchList(props: Props) {
  const { location, isMatchesView, selectedLocation } = props;
  const matches = useAppSelector((state) => state.matches.groupMatches);
  const tournamentEvents = useAppSelector((state) => state.tournamentEvents.tournamentEvents);
  const [isMatchUpdateDialogOpen, setIsMatchUpdateDialogOpen] = useState(false);
  const [isTournamentEventUpdateDialogOpen, setIsTournamentEventUpdateDialogOpen] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<{ puljeId: string; matchId: string }>();
  const [selectedMatch, setSelectedMatch] = useState<Match>();
  const [selectedTournamentEvent, setSelectedTournamentEvent] = useState<TournamentEvent>();
  const favoriteTeams = useAppSelector((state) => state.teams.favoriteTeams);
  const lastUpdatedMatch = useAppSelector((state) => state.matches.lastUpdatedMatch);
  const isAdmin = useAppSelector((state) => state.app.admin);
  const navigate = useNavigate();

  // MATCHES
  const filteredMatches = useMemo(() => {
    let returnObj = [...matches, ...tournamentEvents].sort((a, b) => a.startTime + a.location.delay * 60 - (b.startTime + b.location.delay * 60));

    if (isMatchesView) {
      return returnObj.filter((match: Match | TournamentEvent) => match.location.id === location?.id);
    } else {
      return returnObj.filter(
        (match: Match | TournamentEvent): match is Match =>
          'homeTeam' in match && 'awayTeam' in match && !!match.homeTeam && !!match.awayTeam && (favoriteTeams?.includes(match.homeTeam.id ?? "") || favoriteTeams?.includes(match.awayTeam.id ?? ""))
      );
    }
  }, [matches, location, favoriteTeams, isMatchesView, tournamentEvents]);

  useEffect(() => {
    if (selectedMatchId?.matchId) {
      const foundItem = filteredMatches?.find((item) => item.id === selectedMatchId.matchId);
      if (foundItem) {
        const isTournamentEvent = !('homeTeam' in foundItem);

        if (isTournamentEvent) {
          setSelectedTournamentEvent(foundItem as TournamentEvent);
          setSelectedMatch(undefined);
          if (isAdmin) {
            setIsTournamentEventUpdateDialogOpen(true);
          }
        } else {
          setSelectedMatch(foundItem as Match);
          setSelectedTournamentEvent(undefined);
          if (isAdmin) {
            setIsMatchUpdateDialogOpen(true);
          } else {
            navigate(ROUTES.MATCH_DETAILS + `/${foundItem.id}`);
          }
        }
      }
    } else {
      setSelectedMatch(undefined);
      setSelectedTournamentEvent(undefined);
      setIsMatchUpdateDialogOpen(false);
      setIsTournamentEventUpdateDialogOpen(false);
    }
  }, [filteredMatches, selectedMatchId, isAdmin, navigate]);


  // Scroll to the first active 'Playing' match. If no matches are playing, go to next pending match.
  useEffect(() => {
    // timeout: Wait for the slider to be done with the animation before scrolling.
    const activeMatch = document.getElementsByClassName(`${STATUS.PLAYING} ${selectedLocation?.id}`)[0];
    const nextPendingMatch = document.getElementsByClassName(`${STATUS.PENDING} ${selectedLocation?.id}`)[0];

    const scrollElement = activeMatch ?? nextPendingMatch;

    if (!scrollElement) return;

    const rect = scrollElement.getBoundingClientRect();

    // Beregn afstanden fra toppen af vinduet til elementet
    const elementTop = rect.top + window.pageYOffset;

    // Beregn den ønskede scrollposition for at placere elementet i midten
    const offset = window.innerHeight / 2 - rect.height / 2;

    // Scroll til den beregnede position
    window.scrollTo({
      top: elementTop - offset,
      left: 0,
      behavior: "smooth", // Glidende scroll
    });
  }, [lastUpdatedMatch, selectedLocation]);

  return (
    <>
      <MatchUpdateDialog
        match={selectedMatch}
        open={isMatchUpdateDialogOpen}
        handleClose={() => setSelectedMatchId(undefined)}
      />
      <TournamentEventUpdateDialog
        event={selectedTournamentEvent}
        open={isTournamentEventUpdateDialogOpen}
        handleClose={() => setSelectedMatchId(undefined)}
      />
      <div className="match-list">
        {filteredMatches && filteredMatches.length > 0 && (
          <>
            <div className="matches-list-header">Matches</div>
            {filteredMatches?.map((match: Match | TournamentEvent) => (
              'homeTeam' in match ? (
                <MatchComponent
                  key={match.id}
                  onClick={(puljeId, matchId) => setSelectedMatchId({ puljeId, matchId })}
                  match={match as Match}
                />
              ) : (
                <TournamentEventComponent
                  key={match.id}
                  onClick={(eventId) => setSelectedMatchId({ puljeId: '', matchId: eventId })}
                  event={match as TournamentEvent}
                />
              )
            ))}
          </>
        )}
      </div>
    </>
  );
}

export default MatchList;
